/**
 * Every in-app route, built in one place.
 *
 * The ids come from the API. They are UUIDs today and nothing suggests they
 * will stop being UUIDs, but a route built by splicing a server-supplied
 * string into a template is the shape that open-redirect bugs take - React
 * Router had one this year where a backslash in a `<Link to>` escaped the
 * origin (GHSA-wrjc-x8rr-h8h6, fixed in the version this app pins). Encoding
 * the segment costs nothing on a UUID and removes the whole class.
 *
 * Having the routes as functions also means a path change is one edit rather
 * than a search for backtick templates.
 */

const segment = (value: string): string => encodeURIComponent(value);

export const paths = {
  home: () => "/",
  projects: () => "/projeler",
  newProject: () => "/projeler/yeni",
  project: (projectId: string) => `/projeler/${segment(projectId)}`,
  projectSessions: (projectId: string) => `/projeler/${segment(projectId)}/cekimler`,
  projectCoverage: (projectId: string) => `/projeler/${segment(projectId)}/kapsama`,
  /**
   * The review queue for photographs whose capture angle nobody has settled.
   *
   * A task rather than a stage, so it is not one of `PROJECT_SECTIONS`. It is
   * reached from whichever coverage screen the subject gets, and it is empty
   * as often as not - `RegionReviewLink` draws nothing while it is.
   *
   * Also linked from the viewpoint panel and from a session, each beside the
   * finding that sends somebody there.
   */
  projectRegionReview: (projectId: string) =>
    `/projeler/${segment(projectId)}/aci-kontrol`,
  /** The correction flow: opens the session form already set to "düzeltme". */
  newCorrectionSession: (projectId: string) =>
    `/projeler/${segment(projectId)}/cekimler?amac=correction`,
  session: (sessionId: string) => `/cekimler/${segment(sessionId)}`,

  /* The remaining four steps of a project's work. Same encoding rule. */
  projectReconstruction: (projectId: string) =>
    `/projeler/${segment(projectId)}/rekonstruksiyon`,
  projectModel: (projectId: string) => `/projeler/${segment(projectId)}/model`,
  projectFiles: (projectId: string) => `/projeler/${segment(projectId)}/dosyalar`,

  /* Support screens, reachable from every page. */
  help: () => "/yardim",
  /**
   * One help topic, opened directly.
   *
   * The help screen reads `?konu=`, so a failure can send the reader to the
   * section that actually covers it rather than to the top of the manual. The
   * id is a literal from `helpContent`, never a value from the API.
   */
  helpTopic: (topic: string) => `/yardim?konu=${encodeURIComponent(topic)}`,
  settings: () => "/ayarlar",
  about: () => "/hakkinda",
} as const;
